import React, { useEffect, useState } from 'react';
import * as microsoftTeams from '@microsoft/teams-js';
import ThreeViewer from './components/ThreeViewer';
import ConfigPage from './components/ConfigPage';

function App() {
  const [context, setContext] = useState(null);
  const [page, setPage] = useState('loading');
  const [meetingJoinUrl, setMeetingJoinUrl] = useState(null);
  const [ssoToken, setSsoToken] = useState(null);

  useEffect(() => {
    async function initTeams() {
      try {
        await microsoftTeams.app.initialize();
        const ctx = await microsoftTeams.app.getContext();
        setContext(ctx);
        console.log('[App] Teams context:', JSON.stringify(ctx, null, 2));

        // --- Capture Teams meeting join URL for ACS Audio Bridge ---
        console.log('[App] === ACS AUDIO DIAGNOSTICS ===');
        console.log('[App] ctx.meeting:', JSON.stringify(ctx.meeting || null));
        console.log('[App] ctx.page:', JSON.stringify(ctx.page || null));
        console.log('[App] ctx.channel:', JSON.stringify(ctx.channel || null));
        console.log('[App] ctx.chat:', JSON.stringify(ctx.chat || null));
        console.log('[App] microsoftTeams.meeting available:', !!microsoftTeams.meeting);
        
        let meetingSupported = false;
        try {
          meetingSupported = microsoftTeams.meeting && typeof microsoftTeams.meeting.getMeetingDetails === 'function';
        } catch(e) { /* not supported */ }
        console.log('[App] meeting.getMeetingDetails available:', meetingSupported);

        // Helper: validate that a URL is an actual Teams join link
        const isValidJoinUrl = (url) => url && typeof url === 'string' && url.startsWith('https://');

        // Path 1: context.meeting might have joinUrl directly (TeamsJS v2.x)
        if (isValidJoinUrl(ctx.meeting?.joinUrl)) {
          console.log('[App] ✅ Path 1: Meeting join URL from context.meeting.joinUrl:', ctx.meeting.joinUrl);
          setMeetingJoinUrl(ctx.meeting.joinUrl);
        }
        // Path 2: Try getMeetingDetails API
        else if (meetingSupported) {
          console.log('[App] Path 2: Trying getMeetingDetails...');
          try {
            microsoftTeams.meeting.getMeetingDetails((err, details) => {
              console.log('[App] getMeetingDetails callback — err:', err, 'details:', JSON.stringify(details, null, 2));
              if (!err && details) {
                // Try every known property path
                const joinUrl = details?.details?.joinUrl
                  || details?.details?.joinWebUrl
                  || details?.meeting?.joinUrl
                  || details?.joinUrl
                  || details?.details?.joinLink
                  || details?.joinWebUrl;
                if (isValidJoinUrl(joinUrl)) {
                  console.log('[App] ✅ Path 2: Meeting join URL from getMeetingDetails:', joinUrl);
                  setMeetingJoinUrl(joinUrl);
                  return;
                }
                console.warn('[App] getMeetingDetails returned no valid joinUrl. Full response:', JSON.stringify(details));
              } else {
                console.warn('[App] getMeetingDetails error:', err);
              }
              // Path 3: Try Graph API through server-side token exchange
              // For now, log clearly that we have NO valid URL
              console.error('[App] ❌ CANNOT GET MEETING JOIN URL — ACS audio bridge will fail.');
              console.error('[App] ctx.meeting.id =', ctx.meeting?.id);
              console.error('[App] The Teams tab needs OnlineMeetings.Read permission or the join URL must be in the meeting context.');
              console.error('[App] WORKAROUND: Copy the meeting join link manually and configure it on the server.');
              // Do NOT pass meeting-id: to bind-teams — it will break the ACS SDK
              // Instead, pass null so the headset knows audio is unavailable
              setMeetingJoinUrl(null);
            });
          } catch (meetingErr) {
            console.warn('[App] getMeetingDetails threw:', meetingErr.message);
            setMeetingJoinUrl(null);
          }
        } else {
          console.error('[App] ❌ getMeetingDetails NOT supported and no joinUrl in context — ACS audio unavailable');
          setMeetingJoinUrl(null);
        }
        console.log('[App] === END ACS AUDIO DIAGNOSTICS ===');

        // --- Get SSO token for Graph API resolution (needed if no direct joinUrl) ---
        try {
          console.log('[App] Requesting SSO token for Graph API...');
          const token = await microsoftTeams.authentication.getAuthToken();
          console.log('[App] ✅ SSO token acquired, length:', token?.length);
          setSsoToken(token);
        } catch (ssoErr) {
          console.warn('[App] SSO token failed (Graph resolution will not work):', ssoErr.message || ssoErr);
        }

        const isConfig = window.location.pathname.includes('/config')
          || ctx.page?.frameContext === 'settings';

        if (isConfig) {
          setPage('config');
        } else {
          setPage('viewer');
        }
      } catch (err) {
        if (window.location.pathname.includes('/config')) {
          setPage('config-standalone');
        } else {
          setPage('standalone');
        }
      }
    }
    initTeams();
  }, []);

  if (page === 'loading') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center',
        alignItems: 'center', height: '100vh',
        background: '#0a0e17', color: '#e2e8f0',
        fontFamily: 'system-ui' }}>
        <div>
          <h2>Humulo Workshop</h2>
          <p style={{ color: '#64748b' }}>Initializing...</p>
        </div>
      </div>
    );
  }

  if (page === 'config' || page === 'config-standalone') {
    return <ConfigPage context={context} />;
  }

  return (
    <ThreeViewer
      context={context}
      isTeams={page === 'viewer'}
      meetingJoinUrl={meetingJoinUrl}
      ssoToken={ssoToken}
    />
  );
}

export default App;
